import type { ReactNode, Ref } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRightIcon,
  CloseIcon,
  DocumentIcon,
  LockIcon,
  SupportIcon
} from "../icons/AppIcons";
import { EditableAvatar } from "../profile/EditableAvatar";

export type ProtectedDrawerActionItem = {
  key: string;
  label: string;
  supportText?: string;
  icon: ReactNode;
  current?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
};

type ProtectedRoleDrawerProps = {
  open: boolean;
  tenantSlug: string;
  actionItems: ProtectedDrawerActionItem[];
  onClose: () => void;
  closeButtonRef?: Ref<HTMLButtonElement>;
  errorMessage?: string | null;
  viewerName: string;
  viewerRoleLabel: string;
  viewerPhotoUrl?: string | null;
  onEditViewerPhoto?: () => void;
};

export function ProtectedRoleDrawer({
  open,
  tenantSlug,
  actionItems,
  onClose,
  closeButtonRef,
  errorMessage,
  viewerName,
  viewerRoleLabel
  ,viewerPhotoUrl = null, onEditViewerPhoto
}: ProtectedRoleDrawerProps) {
  return (
    <>
      <button
        type="button"
        className="family-drawer-backdrop"
        data-open={open}
        aria-label="メニューを閉じる"
        onClick={onClose}
      />

      <aside
        className="family-drawer"
        data-open={open}
        role="dialog"
        aria-modal="true"
        aria-label="メニュー"
      >
        <div className="family-drawer__header">
          <h2 className="family-drawer__title">メニュー</h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="family-drawer__close"
            aria-label="メニューを閉じる"
            onClick={onClose}
          >
            <CloseIcon size={28} />
          </button>
        </div>

        <div className="family-drawer__content">
          <section className="family-drawer__viewer" aria-label="ログイン中のアカウント">
            <EditableAvatar displayName={viewerName} photoUrl={viewerPhotoUrl} size={52} editable={Boolean(onEditViewerPhoto)} onEdit={() => onEditViewerPhoto?.()} />
            <span className="family-drawer__viewer-main">
              <span className="family-drawer__viewer-kicker">ログイン中</span>
              <span className="family-drawer__viewer-name">{viewerName}</span>
              <span className="family-drawer__viewer-role">{viewerRoleLabel}</span>
            </span>
          </section>
          <section className="family-drawer__section" aria-labelledby="protected-drawer-role-switch">
            <h3 id="protected-drawer-role-switch" className="family-drawer__section-title">
              ロール切替
            </h3>
            <div className="family-drawer__menu">
              {actionItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="family-drawer__row"
                  disabled={item.disabled}
                  onClick={item.onSelect}
                >
                  <span className="family-drawer__row-icon">{item.icon}</span>
                  <span className="family-drawer__row-main">
                    <span className="family-drawer__row-label">{item.label}</span>
                    {item.supportText ? <span className="family-drawer__row-support">{item.supportText}</span> : null}
                  </span>
                  {item.current ? (
                    <span className="family-drawer__status">表示中</span>
                  ) : (
                    <ChevronRightIcon size={20} />
                  )}
                </button>
              ))}
            </div>
            {errorMessage ? <p className="family-drawer__error">{errorMessage}</p> : null}
          </section>

          <section className="family-drawer__section" aria-labelledby="protected-drawer-legal">
            <h3 id="protected-drawer-legal" className="family-drawer__section-title">
              ご案内
            </h3>
            <div className="family-drawer__menu">
              <button type="button" className="family-drawer__row" aria-disabled="true" disabled>
                <span className="family-drawer__row-icon">
                  <DocumentIcon size={20} />
                </span>
                <span className="family-drawer__row-main">
                  <span className="family-drawer__row-label">利用規約</span>
                </span>
                <span className="family-drawer__status family-drawer__status--muted">準備中</span>
              </button>

              <button type="button" className="family-drawer__row" aria-disabled="true" disabled>
                <span className="family-drawer__row-icon">
                  <LockIcon size={20} />
                </span>
                <span className="family-drawer__row-main">
                  <span className="family-drawer__row-label">プライバシーポリシー</span>
                </span>
                <span className="family-drawer__status family-drawer__status--muted">準備中</span>
              </button>

              <Link to={`/t/${tenantSlug}/support`} className="family-drawer__row" onClick={onClose}>
                <span className="family-drawer__row-icon">
                  <SupportIcon size={20} />
                </span>
                <span className="family-drawer__row-main">
                  <span className="family-drawer__row-label">サポート</span>
                </span>
                <ChevronRightIcon size={20} />
              </Link>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}
